import type { AuthService, AuthConfig, Auth0Config } from "./authService";
import { Auth0Adapter } from "./auth0Adapter";
import { MockAdapter } from "./mockAdapter.ts";
import { StubAuth0Adapter } from "./stubAuth0Adapter.ts";
import { DevTokenAdapter } from "./devTokenAdapter";
import { getConfig } from "@/services/config.ts";

let authServiceInstance: AuthService | null = null;

export function createAuthService(
  config?: Partial<
    AuthConfig & {
      mockMode?: string;
      useMockAuth?: boolean;
      useDevToken?: boolean;
      useAuth0Stub?: boolean;
    }
  >,
): AuthService {
  // Return existing instance if already created
  if (authServiceInstance) {
    return authServiceInstance;
  }

  const mockMode = config?.mockMode ?? getConfig().VITE_MOCK_MODE;
  const shouldUseMockAuth =
    (config?.useMockAuth ?? getConfig().VITE_USE_STUB === "true") ||
    mockMode === "full";
  const useDevToken =
    config?.useDevToken ?? getConfig().VITE_USE_DEV_TOKEN === "true";
  const useAuth0Stub =
    config?.useAuth0Stub ?? getConfig().VITE_USE_MINIKUBE_AUTH === "true";

  const auth0Config: Auth0Config = config?.auth0Config ?? {
    domain: getConfig().VITE_AUTH0_DOMAIN,
    clientId: getConfig().VITE_AUTH0_CLIENT_ID,
    audience: getConfig().VITE_AUTH0_AUDIENCE_URL,
    callbackUrl: getConfig().VITE_AUTH0_CALLBACK_URL || "/callback",
  };

  if (shouldUseMockAuth) {
    authServiceInstance = new MockAdapter({});
  } else if (useDevToken) {
    authServiceInstance = new DevTokenAdapter({});
  } else if (useAuth0Stub) {
    authServiceInstance = new StubAuth0Adapter({ auth0Config });
  } else {
    authServiceInstance = new Auth0Adapter({ auth0Config });
  }

  return authServiceInstance;
}

export function getAuthService(): AuthService {
  if (!authServiceInstance) {
    authServiceInstance = createAuthService();
  }
  return authServiceInstance;
}

export function clearAuthService(): void {
  authServiceInstance = null;
}

export function requiresRealAuthEndpoints(): boolean {
  const service = getAuthService();
  return service.requiresRealAuthEndpoints?.() ?? false;
}

export function getAuthDomain(): string | undefined {
  if (!requiresRealAuthEndpoints()) {
    return undefined;
  }
  return getConfig().VITE_AUTH0_DOMAIN;
}

export type {
  AuthService,
  AuthState,
  AuthUser,
  AuthConfig,
} from "./authService";
export { Auth0Adapter } from "./auth0Adapter";
